import { z } from "zod";
import { useQuery, useMutation } from "react-query";
import { useMemo } from "react";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { zodParseWithLog } from "src/api/apiErrorLogs";

// Base endpoint for the resource
const baseUrl = "/api/entity_target_score_assignments" as const;

/**
 * Fetches all Entity Target Score Assignments from the API.
 * Defaults to ordering by label in ascending order.
 */
export const useGetAllEntityTargetScoreAssignments = () => {
    const url = `${baseUrl}?order[label]=asc` as const;

    const query = useQuery({
        queryKey: "getAllEntityTargetScoreAssignments",
        cacheTime: 15 * 60 * 1000, // 15 minutes
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        queryFn: async () => {
            return await loggedInApiCall.get(url);
        },
        select: (response) => {
            const items = response.data["hydra:member"];
            return zodParseWithLog(
                items,
                EntityTargetScoreAssignmentSchema.array(),
                "getAllEntityTargetScoreAssignments",
            );
        },
    });

    // Stabilize the empty array reference to prevent unnecessary re-renders
    const assignments = useMemo(() => {
        return query.data ?? [];
    }, [query.data]);

    return {
        assignments,
        ...query,
    };
};

/**
 * Fetches all Entity Target Score Assignments grouped by entity class.
 */
export const useGetAllEntityTargetScoreAssignmentsGroupedByEntityClass = () => {
    return useQuery({
        queryKey: ["getAllEntityTargetScoreAssignmentsGroupedByEntityClass"],
        queryFn: async () => {
            const response = await loggedInApiCall.get(baseUrl);
            const items: EntityTargetScoreAssignment[] = response.data["hydra:member"];

            // Group by entityClass
            const grouped: Record<string, EntityTargetScoreAssignment[]> = {};
            items.forEach((item) => {
                if (!grouped[item.entityClass]) {
                    grouped[item.entityClass] = [];
                }
                grouped[item.entityClass].push(item);
            });

            return grouped;
        },
    });
};

/**
 * Mutation hook for creating a new assignment (POST) or updating an existing one (PUT).
 */
export const useCreateOrUpdateEntityTargetScoreAssignment = () => {
    return useMutation({
        mutationFn: async (assignment: EntityTargetScoreAssignmentWrite) => {
            return assignment?.id === undefined ?
                    await loggedInApiCall.post(baseUrl, assignment)
                :   await loggedInApiCall.put(`${baseUrl}/${assignment.id}`, assignment);
        },
    });
};

/**
 * Mutation hook for deleting an assignment by its ID.
 */
export const useDeleteEntityTargetScoreAssignment = () => {
    return useMutation({
        mutationFn: async (id: number) => {
            return await loggedInApiCall.delete(`${baseUrl}/${id}`);
        },
    });
};

/**
 * Mutation hook for checking if an assignment is in use.
 */
export const useCheckEntityTargetScoreAssignmentUsage = () => {
    return useMutation({
        mutationFn: async (id: number) => {
            return await loggedInApiCall.get(`${baseUrl}/${id}/check-usage`);
        },
    });
};

export type EntityTargetScoreAssignment = {
    "@id"?: string;
    id: number;
    entityClass: string;
    identifier: string;
    label: string;
    value?: number | null | undefined;
    description?: string;
    scoreId?: number;
    canView?: boolean;
    canModify?: boolean;
    canDelete?: boolean;
};

export const EntityTargetScoreAssignmentSchema: z.ZodSchema<EntityTargetScoreAssignment> = z.object({
    "@id": z.string().optional(),
    id: z.number(),
    entityClass: z.string(),
    identifier: z.string(),
    label: z.string(),
    value: z.number().nullable().optional(),
    description: z.string().optional(),
    scoreId: z.number().optional(),
    canView: z.boolean().optional(),
    canModify: z.boolean().optional(),
    canDelete: z.boolean().optional(),
});

export type EntityTargetScoreAssignmentWrite = {
    id?: number;
    entityClass: string;
    identifier: string;
    label: string;
    value?: number | null | undefined;
    description?: string;
    scoreId?: number;
};

export const EntityTargetScoreAssignmentWriteSchema: z.ZodSchema<EntityTargetScoreAssignmentWrite> = z.object({
    id: z.number().optional(),
    entityClass: z.string(),
    identifier: z.string(),
    label: z.string(),
    value: z.number().nullable().optional(),
    description: z.string().optional(),
    scoreId: z.number().optional(),
});
